import type { Identity } from '../components/header/models/identity';

/**
 * HA-447: a support "view as" tab. A SystemAdmin starts a session in the admin console, which
 * opens this app in a new tab at /view-as#s=<token>. That tab then acts as the viewed practice
 * member, read-only, with the session's own token, never the admin's cookies.
 *
 * The session lives in sessionStorage, so it belongs to the one tab and is gone when the tab
 * closes. Cookies are shared by every tab on the origin and stay the admin's: a view-as tab never
 * reads, refreshes or clears them.
 */

export const VIEW_AS_PATH = '/view-as';

/** Fired when the API refuses a request as read-only, for the banner's notice. */
export const VIEW_AS_READ_ONLY_EVENT = 'healio:view-as-read-only';

/** Fired when the session ends, so the tab swaps the app for the "ended" screen. */
export const VIEW_AS_ENDED_EVENT = 'healio:view-as-ended';

/** The API's 403 code for a write attempted in a session. */
export const VIEW_AS_READ_ONLY_CODE = 'VIEW_AS_READ_ONLY';

const STORAGE_KEY = 'healio:viewAs';
const HANDOFF_PREFIX = '#s=';
const TOKEN_PATTERN = /^hva_[A-Za-z0-9_-]{43}$/;

export interface ViewAsSessionInfo {
  sessionId: string;
  /** ISO 8601, UTC. */
  expiresAt: string;
  /** The viewed member's ID-token claims, for display only; the API never trusts them. */
  claims: Record<string, string>;
}

interface ViewAsState {
  /** Null once the session has ended. */
  token: string | null;
  session?: ViewAsSessionInfo;
}

const read = (): ViewAsState | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as ViewAsState) : null;
  } catch {
    return null;
  }
};

const write = (state: ViewAsState): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage blocked: the tab can't hold a session. getViewAs() then reads nothing, and the
    // start screen reports the session as unavailable.
  }
};

/** True for the whole life of a view-as tab, including after its session ended. */
export const isViewAs = (): boolean => read() !== null;

/** The session's token; null outside a view-as tab and once the session has ended. */
export const getViewAsToken = (): string | null => read()?.token ?? null;

export const getViewAsSession = (): ViewAsSessionInfo | null => read()?.session ?? null;

export const isViewAsEnded = (): boolean => {
  const state = read();

  return state !== null && state.token === null;
};

/**
 * Takes the token from a /view-as#s=<token> handoff and removes it from the address bar before
 * anything else (Sentry, the router, analytics) sees the URL. The fragment is never sent to a
 * server. Returns whether a handoff was taken.
 */
export const captureViewAsHandoff = (): boolean => {
  const { pathname, hash, search } = window.location;

  if (pathname.toLowerCase() !== VIEW_AS_PATH || !hash.startsWith(HANDOFF_PREFIX)) {
    return false;
  }

  window.history.replaceState(null, '', `${pathname}${search}`);

  let token: string;

  try {
    token = decodeURIComponent(hash.slice(HANDOFF_PREFIX.length));
  } catch {
    return false;
  }
  if (!TOKEN_PATTERN.test(token)) {
    return false;
  }

  write({ token });

  return true;
};

export const setViewAsSession = (session: ViewAsSessionInfo): void => {
  const state = read();

  if (state?.token) {
    write({ ...state, session });
  }
};

/** Ends the tab's session locally and tells the app to show the "ended" screen. */
export const markViewAsEnded = (): void => {
  const state = read();

  if (!state || state.token === null) {
    return;
  }
  write({ token: null, session: state.session });
  window.dispatchEvent(new Event(VIEW_AS_ENDED_EVENT));
};

/**
 * The viewed member's identity, in the shape getIDTokenData() returns, with the session's expiry
 * as `exp`. Empty once the session has ended.
 */
export const getViewAsIdentity = (): Identity => {
  const state = read();

  if (!state?.token || !state.session) {
    return {} as Identity;
  }

  return {
    ...(state.session.claims as Identity),
    exp: Math.floor(Date.parse(state.session.expiresAt) / 1000),
  };
};

/** Ms until the session expires; null without a session. */
export const getViewAsMsRemaining = (): number | null => {
  const session = getViewAsSession();

  return session ? Date.parse(session.expiresAt) - Date.now() : null;
};
